// @ts-check
// Estado de sesión en memoria (TSK-0082, MOD-FRONTEND-CORE). Guarda el
// usuario autenticado actual y el token CSRF vigente SOLO en variables de
// módulo (nunca `localStorage`/`sessionStorage`/una cookie legible por JS,
// docs/03-frontend.md §11.3 "no lo guarda como secreto persistente en
// localStorage"): la app es multi-página sin router SPA (confirmado en
// `frontend/src/app/bootstrap.js`, que no monta ningún shell de sesión
// global, y en `frontend/src/pages/diagram-editor-page.js`, que es su
// propio punto de entrada independiente sin estado compartido entre
// páginas) -- este estado se pierde por diseño en cada carga de página
// (recarga completa del documento HTML reinicia el módulo ES), y cada
// página debe volver a llamar a `cargarSesionActual()` al arrancar para
// recuperarlo desde `GET /auth/me`. No es un bug de este módulo: es la
// arquitectura confirmada por esas dos páginas.
//
// Este módulo es la única pieza de `state` de esta tarea (docs/03-
// frontend.md: "components no llaman fetch; services no tocan el DOM"):
// orquesta `auth-client.js` (la capa de red) para mantener el estado
// coherente, pero no toca el DOM en ningún punto -- eso es responsabilidad
// de las páginas/features que lo consuman (TSK-0083/0084/0085).

import { ApiError } from '../services/api-errors.js';
import {
  cerrarSesion as cerrarSesionRemota,
  iniciarSesion as iniciarSesionRemota,
  obtenerUsuarioActual,
} from '../services/auth-client.js';

/** @typedef {import('../services/auth-client.js').UsuarioPublico} UsuarioPublico */
/** @typedef {import('../services/auth-client.js').Credenciales} Credenciales */

// Variables de módulo: única forma de persistencia permitida para estos
// datos (ver docstring). No se exportan directamente -- solo a través de
// los getters de abajo, para que nada externo pueda reasignarlas.
/** @type {UsuarioPublico | null} */
let usuarioActual = null;
/** @type {string | null} */
let csrfTokenActual = null;

/**
 * Sustituye el estado en memoria (login correcto / `cargarSesionActual`
 * correcto). Función privada del módulo: nada externo puede invocarla
 * directamente, solo a través de los flujos de `iniciarSesion`/
 * `cargarSesionActual` de más abajo.
 * @param {UsuarioPublico} usuario
 * @param {string} csrfToken
 */
function establecerSesion(usuario, csrfToken) {
  usuarioActual = { ...usuario };
  csrfTokenActual = csrfToken;
}

/** Limpia el estado en memoria (logout, "no autenticado", o error). */
function limpiarSesion() {
  usuarioActual = null;
  csrfTokenActual = null;
}

/**
 * Usuario autenticado actual, o `null` si no hay sesión cargada en memoria.
 * Devuelve una copia superficial: quien la reciba no puede mutar el estado
 * interno del store a través del objeto devuelto.
 * @returns {UsuarioPublico | null}
 */
export function obtenerUsuario() {
  return usuarioActual === null ? null : { ...usuarioActual };
}

/**
 * Token CSRF vigente, o `null` si no hay sesión cargada en memoria. Expuesto
 * como getter de solo lectura (criterio de aceptación): no existe una
 * propiedad pública mutable equivalente.
 * @returns {string | null}
 */
export function obtenerCsrfToken() {
  return csrfTokenActual;
}

/**
 * Helper para adjuntar el header CSRF (`X-CSRF-Token`, docs/02-backend.md
 * §9.4, `app.dependencies.security.verify_csrf`) a las llamadas mutadoras
 * (login ya no lo necesita -- no hay sesión previa que proteger --, pero
 * logout y cualquier mutación futura de otras páginas sí). Si no hay token
 * vigente devuelve `{}` (ninguna cabecera) en vez de mandar la cadena
 * literal `"null"`, que `verify_csrf` rechazaría igual pero de forma menos
 * clara en logs de request.
 *
 * `apiRequest` (`services/api-client.js`) reenvía `options.headers` al
 * `fetch` subyacente combinándolos con `Accept`/`Content-Type` (ver su
 * `RequestOptions`), así que la cabecera construida aquí sí llega a la red
 * en `auth-client.js#cerrarSesion` (deuda técnica ya resuelta; ampliación de
 * alcance de TSK-0082 que incorporó `services/api-client.js` a sus
 * `rutas_permitidas` para poder arreglarlo en la misma tarea).
 * @returns {Record<string, string>}
 */
export function cabeceraCsrf() {
  return csrfTokenActual === null ? {} : { 'X-CSRF-Token': csrfTokenActual };
}

/**
 * Consulta `GET /auth/me` (vía `auth-client.js#obtenerUsuarioActual`) y
 * sincroniza el store con el resultado. Distingue explícitamente (criterio
 * de aceptación):
 *   - "no autenticado" (401 -> `ApiError` con `kind === 'unauthorized'`):
 *     limpia el store y devuelve `null`, SIN lanzar. Es el caso normal de
 *     una página cargada sin sesión previa, no un error.
 *   - cualquier otro fallo (red, timeout, 5xx, etc.): limpia el store (no
 *     deja un estado de sesión potencialmente obsoleto) y vuelve a lanzar
 *     el mismo `error` sin transformarlo ni registrarlo (ni aquí ni en
 *     ningún punto de este módulo hay `console.*`: `ApiError` no debería
 *     contener password/token, pero este módulo tampoco decide cómo se
 *     muestra el fallo -- eso es responsabilidad de la página que llama),
 *     para que quien invoque `cargarSesionActual()` decida cómo mostrarlo
 *     -- no se confunde con "no autenticado", que sí es un resultado válido
 *     y esperado.
 * @returns {Promise<UsuarioPublico | null>}
 */
export async function cargarSesionActual() {
  try {
    const estado = await obtenerUsuarioActual();
    establecerSesion(estado.usuario, estado.csrfToken);
    return estado.usuario;
  } catch (error) {
    if (error instanceof ApiError && error.kind === 'unauthorized') {
      limpiarSesion();
      return null;
    }
    limpiarSesion();
    throw error;
  }
}

/**
 * Inicia sesión y sincroniza el store: llama a
 * `auth-client.js#iniciarSesion` y, solo si tiene éxito, recupera el perfil
 * completo con `cargarSesionActual()` (el cuerpo de `POST /auth/login` no
 * incluye el perfil, solo `usuario_id`/`csrf_token` -- ver docstring de
 * `LoginResponse` en `backend/src/app/schemas/auth.py` -- así que hace falta
 * ese segundo paso para tener `usuario` completo en el store).
 *
 * Si `iniciarSesionRemota` falla (credenciales incorrectas, red, etc.), el
 * error se propaga sin capturarlo y el store NO se modifica (criterio de
 * aceptación "login incorrecto no guarda nada"): no se llega a invocar
 * `cargarSesionActual()` en absoluto.
 * @param {Credenciales} credenciales
 * @returns {Promise<UsuarioPublico | null>}
 */
export async function iniciarSesion(credenciales) {
  await iniciarSesionRemota(credenciales);
  return cargarSesionActual();
}

/**
 * Cierra sesión: llama a `auth-client.js#cerrarSesion` con el token CSRF
 * vigente y, solo si el backend confirma el cierre (revocación
 * server-side, ADR-0003), limpia el store. Si la llamada remota falla, el
 * error se propaga y el store se deja intacto -- ADR-0003 exige que un
 * logout nunca dependa solo de que el cliente borre su estado local, así
 * que no hay que fingir localmente una sesión cerrada que el servidor no
 * confirmó.
 * @returns {Promise<void>}
 */
export async function cerrarSesion() {
  await cerrarSesionRemota({ csrfToken: obtenerCsrfToken() ?? '' });
  limpiarSesion();
}
